import React from 'react';
import PropTypes from 'prop-types';

/**
 * ToggleSwitch Component
 *
 * A modern iOS-style toggle switch component with smooth animations.
 * Supports dark mode and disabled states.
 *
 * @param {boolean} checked - Whether the toggle is on or off
 * @param {function} onChange - Callback function when toggle is clicked
 * @param {string} label - Optional label text
 * @param {string} labelPosition - Position of label ('left' or 'right')
 * @param {boolean} disabled - Whether the toggle is disabled
 * @param {string} onLabel - Label to show when toggle is ON
 * @param {string} offLabel - Label to show when toggle is OFF
 */
export default function ToggleSwitch({
  checked = false,
  onChange,
  label = '',
  labelPosition = 'right',
  disabled = false,
  onLabel = '',
  offLabel = '',
}) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onChange(!checked);
      }
    }
  };

  const toggleSwitch = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label || (checked ? onLabel : offLabel) || 'Toggle switch'}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={handleKeyDown}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
        dark:focus:ring-offset-gray-800
        ${checked
          ? 'bg-brand-600 dark:bg-brand-500'
          : 'bg-gray-300 dark:bg-gray-600'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white shadow-md
          transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );

  const statusLabel = (
    <span className={`
      text-sm font-medium
      ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}
    `}>
      {checked ? (onLabel || 'ON') : (offLabel || 'OFF')}
    </span>
  );

  if (!label && !onLabel && !offLabel) {
    return toggleSwitch;
  }

  return (
    <div className="flex items-center gap-3">
      {labelPosition === 'left' && label && (
        <span className={`
          text-sm font-medium
          ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}
        `}>
          {label}
        </span>
      )}

      {toggleSwitch}

      {labelPosition === 'right' && label && (
        <span className={`
          text-sm font-medium
          ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}
        `}>
          {label}
        </span>
      )}

      {(onLabel || offLabel) && statusLabel}
    </div>
  );
}

ToggleSwitch.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  disabled: PropTypes.bool,
  onLabel: PropTypes.string,
  offLabel: PropTypes.string,
};
